/**
 * Scenarios – PromoteDialog and PromotedBanner
 *
 * PromoteDialog:
 *   - Requires an explicit reason.
 *   - Clearly states this creates a *Working Forecast for Review* — the
 *     authoritative Forecast is not overwritten.
 *   - Cache invalidation uses the scope-aware listParams passed from the page.
 *
 * PromotedBanner:
 *   - Confirms the promoted forecast run ID.
 *   - Directs user to Review / Forecast for the approval workflow.
 */

import { useState } from "react";
import { AlertCircle, Check, Loader2, X } from "lucide-react";
import {
  usePromoteScenario,
  getListScenariosQueryKey,
  getGetScenarioQueryKey,
} from "@workspace/api-client-react";
import type { Scenario, ScenarioPromotion } from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { formatVariance } from "@/components/tekledger-ui";
import { useBvaPermission } from "@/framework/microapp-context";
import { ACTOR } from "./types";
import type { ScenarioScope } from "./types";
import {
  summarizeTiers,
  summarizeTrueUp,
} from "@/components/license-economics/types";

// ─── PromoteDialog ─────────────────────────────────────────────────────────────

export function PromoteDialog({
  scenario,
  scope,
  onClose,
  onSuccess,
}: {
  scenario: Scenario;
  scope: ScenarioScope;
  onClose: () => void;
  onSuccess: (promotion: ScenarioPromotion) => void;
}) {
  const canPublish = useBvaPermission("BvA31:publish");
  const [reason, setReason] = useState("");
  const promoteMutation = usePromoteScenario();
  const qc = useQueryClient();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason.trim()) return;

    promoteMutation.mutate(
      {
        scenarioId: scenario.scenarioId,
        data: { actor: ACTOR, reason: reason.trim() },
      },
      {
        onSuccess: (promotion) => {
          qc.invalidateQueries({
            queryKey: getListScenariosQueryKey(scope),
          });
          qc.invalidateQueries({
            queryKey: getGetScenarioQueryKey(scenario.scenarioId),
          });
          onSuccess(promotion);
        },
      }
    );
  };

  return (
    <div className="sc-dialog-overlay" data-testid="promote-dialog">
      <div className="sc-dialog">
        <div className="sc-dialog__head">
          <h3>Promote to Working Forecast for Review</h3>
          <button
            className="sc-icon-btn"
            onClick={onClose}
            data-testid="button-close-promote-dialog"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="sc-dialog__body">
          {/* Scenario identity */}
          <div className="sc-promote-identity" data-testid="promote-baseline-identity">
            <div>
              <span className="sc-label">Scenario</span>
              <strong>{scenario.name}</strong>
            </div>
            <div>
              <span className="sc-label">Baseline Forecast Run</span>
              <code>{scenario.baselineForecastRunId.slice(0, 12)}…</code>
            </div>
            <div>
              <span className="sc-label">FY Impact</span>
              <strong
                className={
                  scenario.fyImpact > 0
                    ? "bad-text"
                    : scenario.fyImpact < 0
                    ? "good-text"
                    : ""
                }
              >
                {formatVariance(scenario.fyImpact)}
              </strong>
            </div>
          </div>

          {scenario.drivers.some((driver) => driver.licenseSnapshot) && (
            <div className="sc-license-snapshot" data-testid="promotion-license-terms">
              <div className="sc-license-snapshot__head">
                <strong>Licensing terms carried to Review</strong>
                <span>Immutable snapshots</span>
              </div>
              {scenario.drivers
                .filter((driver) => driver.licenseSnapshot)
                .map((driver) => {
                  const snapshot = driver.licenseSnapshot!;
                  return (
                    <div key={driver.driverId}>
                      <span>
                        {snapshot.vendor} · Seat:{" "}
                        {summarizeTiers(snapshot.seatTiers, snapshot.seatRate, "seat")}
                      </span>
                      <span>
                        Usage:{" "}
                        {summarizeTiers(
                          snapshot.usageTiers,
                          snapshot.unitRate,
                          snapshot.usageUnit || "unit"
                        )}
                      </span>
                      <span>True-up: {summarizeTrueUp(snapshot)}</span>
                    </div>
                  );
                })}
            </div>
          )}

          {/* Intent notice */}
          <div className="sc-promote-notice" data-testid="promote-notice">
            <AlertCircle size={13} />
            <div>
              <strong>This creates a Working Forecast for Review.</strong>
              <p>
                Promoting this scenario creates a new working forecast run
                derived from the authoritative baseline. The authoritative
                Forecast is not overwritten. The promoted run enters the Review
                stage where it can be inspected and, if approved, adopted as
                the new authoritative Forecast.
              </p>
            </div>
          </div>

          {/* Reason form */}
          <form onSubmit={handleSubmit} className="sc-promote-form">
            <div className="sc-field">
              <label htmlFor="promote-reason" className="sc-label">
                Reason for promotion <span className="sc-required">*</span>
              </label>
              <textarea
                id="promote-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Describe why this scenario should become the working forecast (e.g. cost reduction plan approved by leadership)"
                required
                rows={3}
                data-testid="input-promote-reason"
              />
            </div>

            {promoteMutation.isError && (
              <div
                className="sc-form-error"
                role="alert"
                data-testid="promote-error"
              >
                <AlertCircle size={13} />
                <span>
                  Promotion failed. Ensure the scenario is reconciled before
                  promoting.
                </span>
              </div>
            )}

            <div className="sc-dialog__actions">
              <button
                type="button"
                className="tl-btn"
                onClick={onClose}
                data-testid="button-cancel-promote"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="tl-btn primary"
                disabled={!canPublish || promoteMutation.isPending || !reason.trim()}
                data-testid="button-confirm-promote"
              >
                {promoteMutation.isPending ? (
                  <>
                    <Loader2 size={12} className="animate-spin" /> Promoting…
                  </>
                ) : (
                  <>Promote to Review</>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ─── PromotedBanner ────────────────────────────────────────────────────────────

export function PromotedBanner({
  promotion,
  onDismiss,
}: {
  promotion: ScenarioPromotion;
  onDismiss: () => void;
}) {
  return (
    <div
      className="sc-promoted-banner"
      role="status"
      data-testid="promoted-banner"
    >
      <div className="sc-promoted-banner__icon">
        <Check size={16} />
      </div>
      <div className="sc-promoted-banner__body">
        <strong>Scenario promoted to Working Forecast for Review</strong>
        <p>
          A new working forecast run{" "}
          <code>{promotion.promotedForecastRunId.slice(0, 8)}…</code> has been
          created. Navigate to Review / Forecast to continue the approval
          workflow. The authoritative baseline Forecast has not been changed.
        </p>
        <span className="sc-label">Promoted by {promotion.promotedBy}</span>
      </div>
      <button
        className="sc-icon-btn"
        onClick={onDismiss}
        data-testid="button-dismiss-promoted-banner"
        aria-label="Dismiss"
      >
        <X size={15} />
      </button>
    </div>
  );
}
